import React, { useState, useEffect, useRef } from 'react';
import { doc, setDoc, addDoc, collection, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { extractVariables, previewTemplate } from '../../utils/templateManager';
import { hasPermission, FEATURE_PERMISSIONS } from '../../utils/permissions';

const SystemSettingEditorModal = ({ template, onClose, onSaved, user }) => {
  const [formState, setFormState] = useState({
    templateId: '',
    name: '',
    category: 'email',
    type: 'html',
    description: '',
    subject: '',
    htmlContent: '',
    textContent: '',
    value: '',
    jsonValue: '',
    variables: [],
    fromName: 'Sofibella',
    fromEmail: 'customerservice@sofibellawear.com',
    replyTo: 'customerservice@sofibellawear.com',
    isActive: true,
    isArchived: false,
    usedBy: [],
    tags: []
  });
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [activeTab, setActiveTab] = useState('editor'); // editor, preview
  
  const quillRef = useRef(null);
  
  // Auto-generate templateId from name (for new templates only)
  useEffect(() => {
    if (!template && formState.name) {
      // Convert to camelCase
      const generatedId = formState.name
        .trim()
        .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
        .split(/\s+/) // Split by whitespace
        .map((word, index) => {
          if (index === 0) {
            // First word is lowercase
            return word.toLowerCase();
          }
          // Capitalize first letter of subsequent words
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join('');
      setFormState(prev => ({ ...prev, templateId: generatedId }));
    }
  }, [formState.name, template]);
  
  // Load template data
  useEffect(() => {
    if (template) {
      setFormState({
        templateId: template.templateId || template.id || '',
        name: template.name || '',
        category: template.category || 'email',
        type: template.type || 'html',
        description: template.description || '',
        subject: template.subject || '',
        htmlContent: template.htmlContent || '',
        textContent: template.textContent || '',
        value: template.value !== undefined ? String(template.value) : '',
        jsonValue: template.jsonValue ? JSON.stringify(template.jsonValue, null, 2) : '',
        variables: template.variables || [],
        fromName: template.fromName || 'Sofibella',
        fromEmail: template.fromEmail || 'customerservice@sofibellawear.com',
        replyTo: template.replyTo || 'customerservice@sofibellawear.com',
        isActive: template.isActive !== false,
        isArchived: template.isArchived || false,
        usedBy: template.usedBy || [],
        tags: template.tags || []
      });
    }
  }, [template]);
  
  // Auto-extract variables from content
  useEffect(() => {
    if (formState.category === 'email') {
      const subjectVars = extractVariables(formState.subject);
      const htmlVars = extractVariables(formState.htmlContent);
      const textVars = extractVariables(formState.textContent);
      
      const allVars = [...new Set([...subjectVars, ...htmlVars, ...textVars])];
      
      setFormState(prev => ({ ...prev, variables: allVars }));
    }
  }, [formState.subject, formState.htmlContent, formState.textContent, formState.category]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission(user, FEATURE_PERMISSIONS.SYSTEM_SETTINGS_MANAGE)) return;
    setSaving(true);
    setError('');
    
    try {
      // Validation
      if (!formState.name?.trim()) {
        throw new Error('System setting name is required');
      }
      
      if (formState.category === 'email' && !formState.subject?.trim()) {
        throw new Error('Email subject is required');
      }
      
      if (formState.category === 'script_variable' && formState.type === 'json') {
        try {
          JSON.parse(formState.jsonValue);
        } catch {
          throw new Error('Invalid JSON format');
        }
      }
      
      // Prepare data
      const templateData = {
        templateId: formState.templateId || formState.name
          .trim()
          .replace(/[^a-zA-Z0-9\s]/g, '')
          .split(/\s+/)
          .map((word, index) => 
            index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(''),
        name: formState.name,
        category: formState.category,
        type: formState.type,
        description: formState.description,
        isActive: formState.isActive,
        isArchived: formState.isArchived,
        usedBy: formState.usedBy,
        tags: formState.tags,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || 'unknown',
        updatedByName: user?.displayName || 'Unknown User'
      };
      
      // Check if document ID already exists (for new settings only)
      if (!template?.id) {
        const docId = templateData.templateId;
        const existingDocRef = doc(db, collectionPath('systemSettings'), docId);
        const existingDoc = await getDoc(existingDocRef);
        
        if (existingDoc.exists()) {
          throw new Error(
            `A system setting with Document ID "${docId}" already exists. ` +
            `Please use a different name or edit the existing setting.`
          );
        }
      }
      
      // Add category-specific fields
      if (formState.category === 'email') {
        templateData.subject = formState.subject;
        templateData.htmlContent = formState.htmlContent;
        templateData.textContent = formState.textContent;
        templateData.variables = formState.variables;
        templateData.fromName = formState.fromName;
        templateData.fromEmail = formState.fromEmail;
        templateData.replyTo = formState.replyTo;
      } else if (formState.category === 'script_variable') {
        if (formState.type === 'json') {
          templateData.jsonValue = JSON.parse(formState.jsonValue);
        } else if (formState.type === 'number') {
          templateData.value = Number(formState.value);
        } else if (formState.type === 'boolean') {
          templateData.value = formState.value === 'true';
        } else {
          templateData.value = formState.value;
        }
      } else if (formState.category === 'config') {
        if (formState.type === 'json') {
          templateData.jsonValue = JSON.parse(formState.jsonValue);
        } else {
          templateData.value = formState.value;
        }
      }
      
      // Save to Firestore
      if (template?.id) {
        // Update existing
        await setDoc(doc(db, collectionPath('systemSettings'), template.id), templateData, { merge: true });
        onSaved(`Setting "${formState.name}" updated successfully`);
      } else {
        // Create new with camelCase document ID
        templateData.createdAt = serverTimestamp();
        templateData.createdBy = user?.uid || 'unknown';
        templateData.createdByName = user?.displayName || 'Unknown User';
        
        // Use templateId as document ID (camelCase)
        const docId = templateData.templateId;
        await setDoc(doc(db, collectionPath('systemSettings'), docId), templateData);
        onSaved(`Setting "${formState.name}" created successfully`);
      }
      
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err.message);
      setSaving(false);
    }
  };
  
  const handleAddTag = () => {
    if (tagInput.trim() && !formState.tags.includes(tagInput.trim())) {
      setFormState(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };
  
  const handleRemoveTag = (tag) => {
    setFormState(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };
  
  const insertVariable = (varName) => {
    if (quillRef.current) {
      const textarea = quillRef.current;
      const cursorPosition = textarea.selectionStart;
      const textBefore = formState.htmlContent.substring(0, cursorPosition);
      const textAfter = formState.htmlContent.substring(cursorPosition);
      const newContent = textBefore + `{{${varName}}}` + textAfter;
      
      setFormState(prev => ({ ...prev, htmlContent: newContent }));
      
      // Restore cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus();
        const newPosition = cursorPosition + varName.length + 4; // +4 for {{ and }}
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
  };
  
  const renderPreview = () => {
    if (formState.category !== 'email') return null;
    
    const preview = previewTemplate(formState);
    
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Subject Preview
          </label>
          <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm">
            {preview.subject || <span className="text-gray-400">No subject</span>}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            HTML Preview
          </label>
          <div className="bg-white border border-gray-200 rounded p-4">
            <div dangerouslySetInnerHTML={{ __html: preview.htmlContent }} />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Text Preview
          </label>
          <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm whitespace-pre-wrap font-mono text-xs">
            {preview.textContent || <span className="text-gray-400">No text version</span>}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-indigo-600 text-white px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-bold">
            {template ? 'Edit Setting' : 'Create New Setting'}
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-2xl leading-none"
          >
            ×
          </button>
        </div>
        
        {/* Tabs (for email templates) */}
        {formState.category === 'email' && (
          <div className="border-b border-gray-200 bg-gray-50">
            <div className="flex px-6">
              <button
                onClick={() => setActiveTab('editor')}
                className={`px-4 py-3 font-semibold text-sm border-b-2 transition-colors duration-150 ${
                  activeTab === 'editor'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Editor
              </button>
              <button
                onClick={() => setActiveTab('preview')}
                className={`px-4 py-3 font-semibold text-sm border-b-2 transition-colors duration-150 ${
                  activeTab === 'preview'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Preview
              </button>
            </div>
          </div>
        )}
        
        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">
                {error}
              </div>
            )}
            
            {activeTab === 'preview' && formState.category === 'email' ? (
              renderPreview()
            ) : (
              <>
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      System Setting Name *
                    </label>
                    <input
                      type="text"
                      value={formState.name}
                      onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      placeholder="e.g., Order Sync Notification"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Document ID
                    </label>
                    <input
                      type="text"
                      value={formState.templateId}
                      readOnly
                      className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm bg-gray-100 cursor-not-allowed"
                      placeholder="Auto-generated from name"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {template ? 'Document ID (cannot be changed)' : 'Auto-generated from system setting name (camelCase)'}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={formState.category}
                      onChange={(e) => setFormState(prev => ({ 
                        ...prev, 
                        category: e.target.value,
                        type: e.target.value === 'email' ? 'html' : 'string'
                      }))}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      disabled={!!template} // Can't change category after creation
                    >
                      <option value="email">📧 Email Template</option>
                      <option value="script_variable">⚙️ Script Variable</option>
                      <option value="config">🔧 Configuration</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Type
                    </label>
                    <select
                      value={formState.type}
                      onChange={(e) => setFormState(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      {formState.category === 'email' && (
                        <>
                          <option value="html">HTML</option>
                          <option value="text">Plain Text</option>
                        </>
                      )}
                      {formState.category !== 'email' && (
                        <>
                          <option value="string">String</option>
                          <option value="number">Number</option>
                          <option value="boolean">Boolean</option>
                          <option value="json">JSON Object</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formState.description}
                    onChange={(e) => setFormState(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    rows="2"
                    placeholder="What is this template used for?"
                  />
                </div>
                
                {/* Email Template Fields */}
                {formState.category === 'email' && (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          From Name
                        </label>
                        <input
                          type="text"
                          value={formState.fromName}
                          onChange={(e) => setFormState(prev => ({ ...prev, fromName: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          From Email
                        </label>
                        <input
                          type="email"
                          value={formState.fromEmail}
                          onChange={(e) => setFormState(prev => ({ ...prev, fromEmail: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Reply-To Email
                        </label>
                        <input
                          type="email"
                          value={formState.replyTo}
                          onChange={(e) => setFormState(prev => ({ ...prev, replyTo: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Subject *
                      </label>
                      <input
                        type="text"
                        value={formState.subject}
                        onChange={(e) => setFormState(prev => ({ ...prev, subject: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        placeholder="Use {{variableName}} for dynamic content"
                        required
                      />
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-semibold text-gray-700">
                          HTML Content
                        </label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => insertVariable('customerName')}
                            className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
                          >
                            + customerName
                          </button>
                          <button
                            type="button"
                            onClick={() => insertVariable('customerName')}
                            className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
                          >
                            + customerName
                          </button>
                        </div>
                      </div>
                      <textarea
                        ref={quillRef}
                        value={formState.htmlContent}
                        onChange={(e) => setFormState(prev => ({ ...prev, htmlContent: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm"
                        rows={15}
                        placeholder="<p>Hello {{customerName}},</p>&#10;<p>Your order has been shipped.</p>"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use HTML tags and <code className="bg-gray-100 px-1 rounded">{'{{variableName}}'}</code> for dynamic content
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Plain Text Version
                      </label>
                      <textarea
                        value={formState.textContent}
                        onChange={(e) => setFormState(prev => ({ ...prev, textContent: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm"
                        rows="6"
                        placeholder="Plain text fallback for email clients that don't support HTML"
                      />
                    </div>
                    
                    {formState.variables.length > 0 && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Detected Variables
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {formState.variables.map((v, idx) => (
                            <span
                              key={idx}
                              className="bg-purple-50 text-purple-700 px-3 py-1 rounded text-sm font-mono"
                            >
                              {`{{${v}}}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {/* Script Variable / Config Fields */}
                {formState.category !== 'email' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Value *
                    </label>
                    {formState.type === 'json' ? (
                      <textarea
                        value={formState.jsonValue}
                        onChange={(e) => setFormState(prev => ({ ...prev, jsonValue: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm"
                        rows="8"
                        placeholder='{"key": "value"}'
                        required
                      />
                    ) : formState.type === 'boolean' ? (
                      <select
                        value={formState.value}
                        onChange={(e) => setFormState(prev => ({ ...prev, value: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        required
                      >
                        <option value="true">true</option>
                        <option value="false">false</option>
                      </select>
                    ) : (
                      <input
                        type={formState.type === 'number' ? 'number' : 'text'}
                        value={formState.value}
                        onChange={(e) => setFormState(prev => ({ ...prev, value: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        placeholder={formState.type === 'number' ? '31' : 'Value...'}
                        required
                      />
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formState.type === 'json' && 'Enter valid JSON object'}
                      {formState.type === 'number' && 'Enter a numeric value'}
                      {formState.type === 'string' && 'Enter a string value'}
                    </p>
                  </div>
                )}
                
                {/* Usage Tracking */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Used By (Files/Pages)
                  </label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formState.usedByInput || ''}
                        onChange={(e) => setFormState(prev => ({ ...prev, usedByInput: e.target.value }))}
                        className="flex-1 border border-gray-300 rounded px-3 py-2"
                        placeholder="e.g., scripts/sendOrderSyncNotification.js"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (formState.usedByInput?.trim() && !formState.usedBy.includes(formState.usedByInput.trim())) {
                              setFormState(prev => ({
                                ...prev,
                                usedBy: [...prev.usedBy, prev.usedByInput.trim()],
                                usedByInput: ''
                              }));
                            }
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (formState.usedByInput?.trim() && !formState.usedBy.includes(formState.usedByInput.trim())) {
                            setFormState(prev => ({
                              ...prev,
                              usedBy: [...prev.usedBy, prev.usedByInput.trim()],
                              usedByInput: ''
                            }));
                          }
                        }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded"
                      >
                        Add
                      </button>
                    </div>
                    {formState.usedBy.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-2 max-h-32 overflow-y-auto">
                        {formState.usedBy.map((usage, idx) => (
                          <div key={idx} className="flex items-center justify-between py-1 px-2 hover:bg-blue-100 rounded group">
                            <span className="text-xs text-blue-800 font-mono">{usage}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setFormState(prev => ({
                                  ...prev,
                                  usedBy: prev.usedBy.filter((_, i) => i !== idx)
                                }));
                              }}
                              className="text-red-600 hover:text-red-800 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      Track which scripts or pages use this template. Press Enter or click Add to add entries.
                    </p>
                  </div>
                </div>
                
                {/* Tags */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tags
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      className="flex-1 border border-gray-300 rounded px-3 py-2"
                      placeholder="Add a tag and press Enter"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formState.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded flex items-center gap-2"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="text-indigo-500 hover:text-indigo-700"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Active and Archive toggles */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formState.isActive}
                      onChange={(e) => setFormState(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <label htmlFor="isActive" className="text-sm text-gray-700">
                      Active (available for use in scripts and features)
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isArchived"
                      checked={formState.isArchived}
                      onChange={(e) => setFormState(prev => ({ ...prev, isArchived: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <label htmlFor="isArchived" className="text-sm text-gray-700">
                      <span className="text-amber-700 font-semibold">Archived</span> (hidden from active view, not available for use)
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? 'Saving...' : template ? 'Update Setting' : 'Create Setting'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SystemSettingEditorModal;

